import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import md5 from 'crypto-js/md5';
import { presetRange } from '../lib/timetrackerStats';

configure({ enforceActions: 'always' });

const defaultPeriod = presetRange('thisYear');

class TimeTrackerStore extends LocalModel {
    constructor() {
        super('timeTracker', {
            namePlural: 'timeTrackers',
            namePluralReal: 'timeTrackers',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/timetrackers/',
                    params: {
                        limit: 15,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/timetrackers/',
                    params: {},
                },
                save: {
                    url: '/api/timetrackers/',
                    params: {},
                },
                delete: {
                    url: '/api/timetrackers/',
                    params: {},
                },
            },
        });
    }

    @observable newTimeTracker = {};

    @observable timeTracker = {};

    @observable timeTrackers = [];

    @observable clientHours = [];

    @observable totalHours = [];

    @observable clientSummary = [];

    @observable billingClientSummary = [];

    @observable periodPreset = 'thisYear';

    @observable periodStartDate = defaultPeriod.startDate;

    @observable periodEndDate = defaultPeriod.endDate;

    @observable runningTimers = [];

    setPeriod({ preset, startDate = '', endDate = '' }) {
        this.updateKeyValue('periodPreset', preset);
        this.updateKeyValue('periodStartDate', startDate);
        this.updateKeyValue('periodEndDate', endDate);
    }

    periodQuery() {
        const query = {};
        if (this.periodStartDate) query.startDate = this.periodStartDate;
        if (this.periodEndDate) query.endDate = this.periodEndDate;
        return query;
    }

    async loadRunning() {
        const response = await util.fetchApi('/api/timetrackers/', { method: 'GET' }, { running: 1, limit: 100 });
        this.updateKeyValue('runningTimers', (response && response.data) || []);
        return response;
    }

    async toggleTimer({ client, project, work }) {
        const response = await util.fetchApi('/api/timetrackers/toggle', { method: 'POST' }, { client, project, work });
        await Promise.all([
            this.load({ addData: ['tags', 'clientHours', 'totalHours', 'clientSummary'], query: this.periodQuery() }),
            this.loadRunning(),
        ]);
        return response;
    }

    async loadBilling({ startDate, endDate } = {}) {
        const query = { addData: 'clientSummary' };
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const response = await util.fetchApi('/api/timetrackers/', { method: 'GET' }, query);
        const summary = (response && response.included && response.included.clientSummary) || [];
        this.updateKeyValue('billingClientSummary', summary);
        return response;
    }

    async linkClientToVisitor({ client, visitor, hourlyRate }) {
        const response = await util.fetchApi('/api/timetrackers/link-client', { method: 'POST' }, { client, visitor, hourlyRate });
        return response;
    }

    timeEntriesToSaleLines({ entries = [], hourlyRate = 0, user = {} }) {
        const now = new Date();
        return entries
            .filter(e => e && e.startTime)
            .map((e) => {
                const start = new Date(e.startTime).getTime();
                const end = e.endTime ? new Date(e.endTime).getTime() : now.getTime();
                const hours = (end - start) / 3600000;
                const dateLabel = util.isoDate(e.startTime, false, false, true);
                const name = `${dateLabel} ${e.description || ''}`.trim();
                return {
                    name,
                    qty: hours,
                    price: Number(hourlyRate) || 0,
                    vat: 25,
                    createdDate: new Date(),
                    md5: md5(JSON.stringify({ name, hours, start })).toString(),
                    saleUser: user.id,
                    saleDepartment: user.currentCustomerDepartment,
                };
            });
    }
}

const store = new TimeTrackerStore();
export default store;
